<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, onMounted, getCurrentInstance, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import _ from 'lodash';
import { track, EVENTS } from '@/lib/tracking'
import Tooltip from '@/components/Atoms/Tooltip.vue';
import StreamService from '@/services/StreamService';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import FormSwitch from '../../components/Atoms/FormSwitch.vue';
import FormButton from '../../components/Atoms/FormButton.vue';
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import IconBase from '../../components/icon/IconBase.vue';
import IconChevronUpDown from '../../components/icon/IconChevronUpDown.vue';
import IconLock from '@/components/icon/IconLock.vue';
import FormInput from '../../components/Atoms/FormInput.vue';

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const props = defineProps({
  stream: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  streamAlive: {
    type: Boolean
  },
})

const emit = defineEmits({
  'set-live-stream-countdown': () => true,
  'live-stream-abr-changed': () => true
})

const fpsDropdown = ref(null)
const streamMeta = ref(null)
const abr = ref(null)
const abrProcessing = ref(false)
const selectedFps = ref(null)
const fpsProcessing = ref(false)
const fpsOpts = ref([
  { name: 25 },
  { name: 30 },
  { name: 50 },
  { name: 60 },
  { name: 'Pass' }
])

const AudioBitrateDropdown = ref(null)
const audioBitrateEnabled = ref(null)
const audioBitrateProcessing = ref(false)
const selectedAudioBitrate = ref(128)
const audioBitrateOpts = ref([
  { name: 64 },
  { name: 128 },
  { name: 256 },
  { name: 320 },
  { name: 512 },
])

const saveAudioBitrate = async () => {
  audioBitrateProcessing.value = true
  await saveSetting('audioBitrate', selectedAudioBitrate.value, true)
  audioBitrateProcessing.value = false
  streamMeta.value.audioBitrate = selectedAudioBitrate.value
}

const onAudioBitrateSelect = (bitrate) => {
  selectedAudioBitrate.value = bitrate.name
  AudioBitrateDropdown.value.toggleDropdown()
}

const customBitrateLadder = ref(null)

const customBitrate = ref({
  'source': { enabled: false, bitrate: '' },
  '1080p': { enabled: false, bitrate: '' },
  '720p': { enabled: false, bitrate: '' },
  '480p': { enabled: false, bitrate: '' },
  '360p': { enabled: false, bitrate: '' },
  '240p': { enabled: false, bitrate: '' },
})

const customBitrateErrors = ref({
  'source': null,
  '1080p': null,
  '720p': null,
  '480p': null,
  '360p': null,
  '240p': null,
}
)

const bitrateRows = [
  { key: 'source', label: 'Source' },
  { key: '1080p', label: '1080p' },
  { key: '720p', label: '720p' },
  { key: '480p', label: '480p' },
  { key: '360p', label: '360p' },
  { key: '240p', label: '240p' },
]
const bitrateProcessing = ref(false)
const featureProcessing = ref(false)
const saveBitrateDisabled = ref(true)
const bypassBitrateValidation = ref(false)

const bitradeLadderFlag = computed(() => store.getters['User/bitradeLadderFlag'])
const audioBitrateLadderFlag = computed(() => store.getters['User/audioBitrateLadderFlag'])

watch(
  () => customBitrate,
  (configNew, configOld) => {
    validateCustomBitrate()
  },
  { deep: true }
)

onMounted(async () => {
  streamMeta.value = await StreamService.getStreamMetadata(props.stream._id)
  customBitrateLadder.value = streamMeta.value.customBitrateLadder
  audioBitrateEnabled.value = streamMeta.value.audioBitrateEnabled
  selectedAudioBitrate.value = streamMeta.value.audioBitrate

  abr.value = streamMeta.value.abr
  selectedFps.value = streamMeta.value.abrFps

  if (streamMeta.value.customBitrate) {
    bypassBitrateValidation.value = true
    customBitrate.value = JSON.parse(streamMeta.value.customBitrate)
  } else {
    streamMeta.value.customBitrate = _.cloneDeep(customBitrate.value)
  }
})

const customBitrateChange = async (bitrate) => {
  if (!customBitrate.value[bitrate].enabled) {
    customBitrate.value[bitrate].bitrate = ''
    customBitrateErrors.value[bitrate] = null
  }
}
const toggleABR = async () => {
  const nstate = abr.value;
  const methodName = nstate === true ? 'enableStreamABR' : 'disableStreamABR';

  abrProcessing.value = true
  try {
    await StreamService[methodName](props.stream._id);
    abr.value = nstate;
    if (streamMeta.value) streamMeta.value.abr = nstate;
    store.dispatch('StreamCache/clearStreamCache', props.stream._id);
    notify({ group: 'success', text: 'Setting saved' })
    track(EVENTS.ABR_TOGGLED, {
      streamId: props.stream._id,
      streamType: props.stream.type,
      abrEnabled: nstate,
    })
    if (methodName === 'disableStreamABR' && customBitrateLadder.value) {
      await saveSetting('customBitrateLadder', false, false)
      customBitrateLadder.value = false
    }
  } catch (e) {
    if (_.includes(_.get(e, 'message'), 'upgrade')) {
      showModal('feature-upgrade');
      abr.value = !nstate;
    } else {
      console.log('api-error', e);
    }

    const errorMsg = e ? e.message : 'could not toggle stream ABR';
    abr.value = !nstate;
    notify({ group: 'error', text: errorMsg });
  }
  abrProcessing.value = false

  emit('live-stream-abr-changed', nstate);
}

const validateCustomBitrate = () => {
  if (bypassBitrateValidation.value) {
    bypassBitrateValidation.value = false
    return
  }

  const objectsEqual = (o1, o2) =>
    typeof o1 === 'object' && Object.keys(o1).length > 0
      ? Object.keys(o1).length === Object.keys(o2).length
      && Object.keys(o1).every(p => objectsEqual(o1[p], o2[p]))
      : o1 === o2;

  if (objectsEqual(customBitrate.value, streamMeta.value.customBitrate)) {
    saveBitrateDisabled.value = true
    return
  }

  const disabled = _.every(Object.values(customBitrate.value), { 'enabled': false })
  if (disabled) {
    saveBitrateDisabled.value = true
    return
  }

  _.map(Object.keys(customBitrate.value), (key, val) => {
    const bitrate = customBitrate.value[key].bitrate
    if (customBitrate.value[key].enabled) {
      if (!isNumeric(bitrate)) {
        customBitrateErrors.value[key] = 'Numbers only'
      } else if (Number(bitrate) > 15000 || Number(bitrate) < 250) {
        customBitrateErrors.value[key] = 'Must be between 250 and 15000 Kbps'
      } else {
        customBitrateErrors.value[key] = null
        saveBitrateDisabled.value = false
      }
    }
  })

  let validation = false
  _.map(Object.values(customBitrateErrors.value), (val, n) => {
    if (val) {
      validation = true
    }
  })

  if (validation) {
    saveBitrateDisabled.value = true
    return
  }

  saveBitrateDisabled.value = false
}

const isNumeric = (str) => {
  if (typeof str === 'number') return true
  if (typeof str != "string") return false
  return !isNaN(str) && !isNaN(parseFloat(str))
}

const saveBitrate = async () => {
  bitrateProcessing.value = true
  await saveSetting('customBitrate', JSON.stringify(customBitrate.value), true)

  streamMeta.customBitrate = _.cloneDeep(customBitrate.value)
  saveBitrateDisabled.value = true
  bitrateProcessing.value = false
}

const saveFps = async () => {
  fpsProcessing.value = true
  await saveSetting('abrFps', selectedFps.value, true)
  fpsProcessing.value = false
  streamMeta.value.abrFps = selectedFps.value
}

const onFpsSelect = (fps) => {
  selectedFps.value = fps.name
  fpsDropdown.value.toggleDropdown()
}

const navigateToBilling = () => {
  if ((this.stream.type === 'live') || this.stream.type === 'vod') {
    window.location.href = '/app/subscribe?category=live';
  } else {
    window.location.href = '/app/subscribe';
  }
}

const toggleFeature = async (featureName, flag) => {
  if (featureName === 'abr') {
    toggleABR()
    return
  }
  await saveSetting(featureName, flag, true)
}

const saveSetting = async (key, value, successToast = false) => {
  featureProcessing.value = true;
  try {
    await StreamService.saveStreamMetadata(props.stream._id, key, value)
    if (successToast) {
      notify({ group: 'success', text: 'Setting saved' })
    }
    track(EVENTS.ABR_SETTING_SAVED, {
      streamId: props.stream._id,
      streamType: props.stream.type,
      settingKey: key,
      settingValue: value,
    })
  } catch (e) {
    notify({ group: 'error', text: 'Unable to save your changes.' })
  }
  featureProcessing.value = false;
}

const ladderTooltip = computed(() => {
  if (props.streamAlive) return 'You cannot change this while the stream is live.'
  if (!abr.value) return 'Adaptive Bitrate needs to be enabled.'
})

const audioLadderTooltip = computed(() => {
  if (!audioBitrateLadderFlag.value) return 'Upgrade plan to enable this feature'
  if (props.streamAlive) return 'You cannot change this while the stream is live.'
  if (!abr.value) return 'Adaptive Bitrate needs to be enabled.'
})

const saveTooltip = `Customization is available only on the Ultra plan. Please <a class="text-underline text-reset" target="_blank"
href="/app/subscribe">upgrade</a>.`

</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Enable Adaptive Bitrate -->
    <section class="bg-surface-2 rounded-lg p-4 flex items-start justify-between gap-6">
      <div class="min-w-0">
        <h4 class="text-sm font-medium text-white mb-1">Enable Adaptive Bitrate</h4>
        <p class="text-tiny text-surface-7 mb-0 leading-relaxed">Dynamically adjusts video quality to ensure seamless
          playback and minimal buffering, optimizing for varying network conditions.</p>
      </div>
      <div class="shrink-0 pt-0.5">
        <Tooltip v-if="props.streamAlive" content="You cannot change this while the stream is live.">
          <FormSwitch :disabled="abrProcessing || props.streamAlive" @change="toggleFeature('abr', abr)" v-model="abr" />
        </Tooltip>
        <FormSwitch v-else :disabled="abrProcessing || props.streamAlive" @change="toggleFeature('abr', abr)"
          v-model="abr" />
      </div>
    </section>

    <!-- Set Custom FPS -->
    <section class="bg-surface-2 rounded-lg p-4">
      <h4 class="text-sm font-medium text-white mb-1">Set Custom FPS</h4>
      <p class="text-tiny text-surface-7 mb-3 leading-relaxed">The default FPS for transcoding is set to 30. If you stream
        at a different FPS, adjust it here.</p>
      <div class="flex gap-x-2">
        <DropDownMenu type="secondary" ref="fpsDropdown">
          <template #toggle-button>
            {{ selectedFps ? selectedFps : 'Select an option' }}
            <icon-base class="!ml-1">
              <icon-chevron-up-down />
            </icon-base>
          </template>
          <template #menu-items>
            <DropDownMenuItem class="uppercase" v-for="(fps, index) in fpsOpts" :key="index" @click="onFpsSelect(fps)">
              {{ fps.name }}
            </DropDownMenuItem>
          </template>
        </DropDownMenu>
        <FormButton v-if="streamMeta" @click="saveFps"
          :disabled="Number(streamMeta.abrFps) === Number(selectedFps) || props.streamAlive || !abr || (streamMeta.abrFps === 'Pass' && selectedFps === 'Pass') || fpsProcessing">
          <span>{{ fpsProcessing ? 'Saving' : 'Save' }}</span>
        </FormButton>
      </div>
    </section>

    <!-- Advanced Transcode Settings -->
    <section class="bg-surface-2 rounded-lg p-4">
      <h4 class="text-sm font-medium text-white mb-1">Advanced Transcode Settings</h4>
      <p class="text-tiny text-surface-7 mb-0 leading-relaxed">Customize the bitrates and add more profiles to the stream
        output. The changes below will override the default Castr bitrate ladder.</p>

      <div class="flex items-center justify-between gap-6 mt-4">
        <h5 class="text-sm font-medium text-white mb-0">Customize Bitrate Ladder</h5>
        <div class="shrink-0">
          <Tooltip v-if="props.streamAlive || !abr" :content="ladderTooltip">
            <FormSwitch @change="toggleFeature('customBitrateLadder', customBitrateLadder)" v-model="customBitrateLadder"
              :disabled="!abr || props.streamAlive" />
          </Tooltip>
          <FormSwitch v-else @change="toggleFeature('customBitrateLadder', customBitrateLadder)"
            v-model="customBitrateLadder" :disabled="!abr || props.streamAlive" />
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 transition-opacity"
        :class="{ 'opacity-60': !customBitrateLadder }">
        <div v-for="row in bitrateRows" :key="row.key" class="min-w-0 bg-surface-1 rounded-lg p-3 ring-1 transition-colors"
          :class="customBitrate[row.key].enabled && customBitrateLadder ? 'ring-main/40' : 'ring-white/[0.05]'">
          <div class="flex items-center justify-between gap-2 mb-2.5">
            <span class="text-sm font-medium transition-colors"
              :class="customBitrate[row.key].enabled && customBitrateLadder ? 'text-white' : 'text-surface-7'">
              {{ row.label }}
            </span>
            <FormSwitch size="sm" v-model="customBitrate[row.key].enabled" @change="customBitrateChange(row.key)"
              :disabled="!customBitrateLadder" />
          </div>
          <FormInput :isError="!!customBitrateErrors[row.key]" class="w-full"
            v-model="customBitrate[row.key].bitrate"
            :disabled="!customBitrateLadder || !customBitrate[row.key].enabled" placeholder="Specify bitrate in Kbps" />
          <p v-if="customBitrateErrors[row.key]" class="mt-1.5 mb-0 text-tiny text-error leading-snug">
            {{ customBitrateErrors[row.key] }}
          </p>
        </div>
      </div>

      <Tooltip v-if="!bitradeLadderFlag" :content="saveTooltip">
        <FormButton class="mr-auto mt-4" :disabled="saveBitrateDisabled || !bitradeLadderFlag" @click="saveBitrate">
          <icon-base v-if="!bitradeLadderFlag" class="!mr-1">
            <icon-lock />
          </icon-base>
          {{ bitrateProcessing ? 'Saving' : 'Save' }}
        </FormButton>
      </Tooltip>
      <FormButton v-else class="mr-auto mt-4"
        :disabled="saveBitrateDisabled || !bitradeLadderFlag || bitrateProcessing" @click="saveBitrate">
        <icon-base v-if="!bitradeLadderFlag" class="!mr-1">
          <icon-lock />
        </icon-base>
        {{ bitrateProcessing ? 'Saving' : 'Save' }}
      </FormButton>
    </section>

    <!-- Set Audio Bitrate -->
    <section class="bg-surface-2 rounded-lg p-4">
      <div class="flex items-start justify-between gap-6">
        <div class="min-w-0">
          <h4 class="text-sm font-medium text-white mb-1">Set Audio Bitrate</h4>
          <p class="text-tiny text-surface-7 mb-0 leading-relaxed">The default audio bitrate for transcoding is set to 128
            kbps. If you stream at a different audio bitrate, adjust it here.</p>
        </div>
        <div class="shrink-0 pt-0.5">
          <Tooltip v-if="props.streamAlive || !abr || !audioBitrateLadderFlag" :content="audioLadderTooltip">
            <FormSwitch @change="toggleFeature('audioBitrateEnabled', audioBitrateEnabled)" v-model="audioBitrateEnabled"
              :disabled="!abr || props.streamAlive || !audioBitrateLadderFlag" />
          </Tooltip>
          <FormSwitch v-else @change="toggleFeature('audioBitrateEnabled', audioBitrateEnabled)"
            v-model="audioBitrateEnabled" :disabled="!abr || props.streamAlive" />
        </div>
      </div>

      <div class="flex gap-x-2 mt-3">
        <DropDownMenu type="secondary" ref="AudioBitrateDropdown" :disabled="!audioBitrateEnabled">
          <template #toggle-button>
            {{ selectedAudioBitrate ? selectedAudioBitrate + ' kbps' : 'Select an option' }}
            <icon-base class="!ml-1">
              <icon-chevron-up-down />
            </icon-base>
          </template>
          <template #menu-items>
            <DropDownMenuItem class="uppercase" v-for="(bitrate, index) in audioBitrateOpts" :key="index"
              @click="onAudioBitrateSelect(bitrate)">
              {{ bitrate.name }} kbps
            </DropDownMenuItem>
          </template>
        </DropDownMenu>
        <FormButton v-if="streamMeta" @click="saveAudioBitrate"
          :disabled="Number(streamMeta.audioBitrate) === Number(selectedAudioBitrate) || props.streamAlive || !abr || audioBitrateProcessing || !audioBitrateEnabled">
          <span>{{ audioBitrateProcessing ? 'Saving' : 'Save' }}</span>
        </FormButton>
      </div>
    </section>
  </div>
  <confirm-modal modal-id="feature-upgrade" ok-text="Upgrade Now" cancel-text="Later"
    @modal-confirm="navigateToBilling()">
    <p class="mb-0 text-s-m"><a target="_blank" class="text-reset text-underline" href="/app/subscribe">Upgrade</a> to
      access this feature. Check <a target="_blank" class="text-reset text-underline text-nowrap"
        href="https://castr.com/pricing">Pricing page</a> to learn more about eligible plans.</p>
  </confirm-modal>
</template>
